import React from 'react';
import { X, User, ChevronRight, FileText, Paperclip, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const LEVEL_COLORS = {
  0: '#2f5be8', 1: '#4a72f0', 2: '#0ab394', 3: '#6340d4',
  4: '#1a9ec0', 5: '#4a9020', 6: '#9098c0',
};

const LEVEL_NAMES = ['Nivel 0','Nivel 1','Nivel 2','Nivel 3','Nivel 4','Nivel 5','Nivel 6'];

function getInitials(name) {
  if (!name || name === 'Vacante') return 'V';
  return name.split(/[\s/]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

export const OrgChartSidePanel = ({ node, allNodes, onClose, onNavigate, userRole }) => {
  if (!node) return null;

  const isVacant = node.nombre === 'Vacante';
  const parent = allNodes.find(n => n.node_id === node.parent_id);
  const children = allNodes.filter(n => n.parent_id === node.node_id);
  const color = isVacant ? '#d93050' : (LEVEL_COLORS[node.level] || '#9098c0');
  const canSeeDetails = userRole === 'admin' || userRole === 'user';
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleOpenAttachment = async (att) => {
    try {
      const res = await fetch(`${API_URL}/api/storage-files/${att}`);
      if (res.status === 410) {
        toast.error('Esta foto/archivo no está disponible (se subió antes de abril 2026 y no se pudo migrar). Puedes volver a subirlo si aún lo tienes.', { duration: 8000 });
        return;
      }
      if (!res.ok) {
        toast.error('No se pudo abrir el archivo');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Error opening orgchart attachment:', e);
      toast.error('No se pudo abrir el archivo');
    }
  };

  return (
    <div
      className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200"
      data-testid="orgchart-side-panel"
    >
      {/* Header */}
      <div className="p-5 border-b border-slate-100" style={{ background: `linear-gradient(135deg, ${color}10, ${color}05)` }}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="orgchart-panel-close"
        >
          <X className="w-5 h-5 text-slate-500" />
        </button>
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
            style={{ backgroundColor: color }}
          >
            {getInitials(node.nombre)}
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 leading-tight">{node.puesto}</h3>
            <p className={cn("text-sm mt-0.5", isVacant ? "text-red-500 font-medium" : "text-slate-600")}>{node.nombre}</p>
            <span
              className="inline-block mt-1.5 px-2 py-0.5 rounded text-xs font-semibold text-white"
              style={{ backgroundColor: color }}
            >
              {LEVEL_NAMES[node.level] || `Nivel ${node.level}`}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Direct manager */}
        {parent && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Jefe Directo</h4>
            <button
              onClick={() => onNavigate(parent.node_id)}
              className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors text-left"
              data-testid="orgchart-panel-parent"
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ backgroundColor: parent.nombre === 'Vacante' ? '#d93050' : (LEVEL_COLORS[parent.level] || '#9098c0') }}
              >
                {getInitials(parent.nombre)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{parent.puesto}</p>
                <p className="text-xs text-slate-500 truncate">{parent.nombre}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </button>
          </div>
        )}

        {/* Direct reports */}
        {children.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
              Subordinados Directos ({children.length})
            </h4>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {children.map(child => (
                <button
                  key={child.node_id}
                  onClick={() => onNavigate(child.node_id)}
                  className="flex items-center gap-3 w-full p-2.5 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors text-left"
                  data-testid={`orgchart-panel-child-${child.node_id}`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: child.nombre === 'Vacante' ? '#d93050' : (LEVEL_COLORS[child.level] || '#9098c0') }}
                  >
                    {getInitials(child.nombre)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{child.puesto}</p>
                    <p className="text-xs text-slate-500 truncate">{child.nombre}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Descripcion del Puesto</h4>
          {canSeeDetails ? (
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 min-h-[60px]">
              {node.description || <span className="text-slate-400 italic">Sin descripcion</span>}
            </div>
          ) : (
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-400 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Visible solo para Usuarios y Administradores</span>
            </div>
          )}
        </div>

        {/* Attachments */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Archivos Adjuntos</h4>
          {canSeeDetails ? (
            (node.attachments && node.attachments.length > 0) ? (
              <div className="space-y-1.5">
                {node.attachments.map((att, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleOpenAttachment(att)}
                    className="w-full flex items-center gap-2 p-2.5 rounded-lg hover:bg-blue-50 border border-slate-100 transition-colors text-sm text-blue-600 text-left"
                    data-testid={`orgchart-attachment-${i}`}
                  >
                    <Paperclip className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{att.split('/').pop()}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-400 italic">
                Sin archivos adjuntos
              </div>
            )
          ) : (
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-400 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Visible solo para Usuarios y Administradores</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
